import React, { useMemo, useState } from 'react';
import { BarChart3, ChevronDown, ChevronRight } from 'lucide-react';

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const toLevel = (percentage) => {
  const pct = toNumber(percentage);
  if (pct >= 80) return 7;
  if (pct >= 70) return 6;
  if (pct >= 60) return 5;
  if (pct >= 50) return 4;
  if (pct >= 40) return 3;
  if (pct >= 30) return 2;
  return 1;
};

const flattenTaskQuestionLabels = (questions = []) => {
  const labels = [];
  questions.forEach((question) => {
    const qLabel = String(question.questionNumber || '').trim();
    if (!qLabel) return;

    const subquestions = Array.isArray(question.subquestions) ? question.subquestions : [];
    if (subquestions.length === 0) {
      labels.push({ label: qLabel, display: `Q${qLabel}` });
      return;
    }

    subquestions.forEach((sq) => {
      const subLabel = String(sq.label || '').trim();
      if (!subLabel) return;
      const full = subLabel.startsWith(qLabel)
        ? subLabel
        : `${qLabel}.${subLabel.replace(/^\./, '')}`;
      labels.push({ label: full, display: `Q${full}` });
    });
  });

  return labels;
};

const collectAnnotations = (job) => {
  const pageResults = Array.isArray(job?.results?.pageResults) ? job.results.pageResults : [];
  return pageResults.flatMap((page) =>
    (Array.isArray(page.annotations) ? page.annotations : []).map((annotation) => ({
      ...annotation,
      pageNumber: page.pageNumber
    }))
  );
};

const scoreColor = (pct) => {
  if (pct >= 70) return 'text-green-700 bg-green-50';
  if (pct >= 50) return 'text-yellow-700 bg-yellow-50';
  return 'text-red-700 bg-red-50';
};

const TaskMarklistAnalysis = ({ markingJobs = [], markingTasks = [], markingQuestions = [] }) => {
  const [selectedTaskId, setSelectedTaskId] = useState('');
  const [expandedLearnerRows, setExpandedLearnerRows] = useState({});

  const selectedTask = useMemo(
    () => markingTasks.find((task) => task.id === selectedTaskId) || null,
    [markingTasks, selectedTaskId]
  );

  const taskQuestions = useMemo(() => {
    if (!selectedTask) return [];
    const questionIds = Array.isArray(selectedTask.questionIds) ? selectedTask.questionIds : [];
    return questionIds
      .map((id) => markingQuestions.find((question) => question.id === id))
      .filter(Boolean);
  }, [selectedTask, markingQuestions]);

  const questionColumns = useMemo(() => {
    const fromTask = flattenTaskQuestionLabels(taskQuestions);
    if (fromTask.length > 0) return fromTask;

    const labels = new Set();
    markingJobs
      .filter((job) => job.taskId === selectedTaskId && job.status === 'completed' && job.results)
      .forEach((job) => {
        collectAnnotations(job).forEach((annotation) => {
          const label = String(annotation.questionLabel || '').trim();
          if (label) labels.add(label);
        });
      });

    return [...labels]
      .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
      .map((label) => ({ label, display: `Q${label}` }));
  }, [taskQuestions, markingJobs, selectedTaskId]);

  const taskJobs = useMemo(() => (
    markingJobs.filter((job) =>
      job.status === 'completed' &&
      job.results &&
      job.taskId === selectedTaskId
    )
  ), [markingJobs, selectedTaskId]);

  const marklistRows = useMemo(() => {
    const learnerJobs = taskJobs.flatMap((job) => job.markingType === 'ai-collaborative' && job.results?.learnerResults
      ? job.results.learnerResults.map((learner) => ({ ...job, id: `${job.id}-${learner.learnerKey}`, learnerId: learner.learnerId,
        learnerName: learner.learnerName, results: learner }))
      : [job]);
    return learnerJobs.map((job) => {
      const annotations = collectAnnotations(job);
      const byQuestion = {};

      questionColumns.forEach((column) => {
        byQuestion[column.label] = {
          awarded: 0,
          available: 0,
          comments: []
        };
      });

      annotations.forEach((annotation) => {
        const label = String(annotation.questionLabel || '').trim();
        if (!label || !byQuestion[label]) return;
        byQuestion[label].awarded += toNumber(annotation.marksAwarded);
        byQuestion[label].available += toNumber(annotation.marksAvailable);
        if (annotation.comment) byQuestion[label].comments.push(annotation.comment);
      });

      const totalScore = toNumber(job.results.totalScore);
      const maxScore = toNumber(job.results.maxScore);
      const percentage = toNumber(job.results.percentage);

      return {
        jobId: job.id,
        learnerName: String(job.learnerName || 'Unknown Learner').trim(),
        learnerId: job.learnerId || null,
        totalScore,
        maxScore,
        percentage,
        level: toLevel(percentage),
        byQuestion
      };
    });
  }, [taskJobs, questionColumns]);

  const learnerReports = useMemo(() => {
    return marklistRows.map((row) => {
      const details = questionColumns.map((column) => {
        const data = row.byQuestion[column.label] || { awarded: 0, available: 0, comments: [] };
        const pct = data.available > 0 ? (data.awarded / data.available) * 100 : 0;
        return {
          label: column.label,
          percentage: pct,
          comments: data.comments
        };
      });

      const strongest = [...details].sort((a, b) => b.percentage - a.percentage).slice(0, 2);
      const weakest = [...details].sort((a, b) => a.percentage - b.percentage).slice(0, 2);

      return {
        ...row,
        strongest,
        weakest,
        recommendation: weakest.length > 0
          ? `Focus revision on ${weakest.map((item) => `Q${item.label}`).join(', ')} with targeted practice and memo correction.`
          : 'Maintain current performance with mixed revision practice.'
      };
    });
  }, [marklistRows, questionColumns]);

  const questionReports = useMemo(() => {
    return questionColumns.map((column) => {
      let awarded = 0;
      let available = 0;
      const comments = [];

      marklistRows.forEach((row) => {
        const data = row.byQuestion[column.label] || { awarded: 0, available: 0, comments: [] };
        awarded += data.awarded;
        available += data.available;
        comments.push(...data.comments);
      });

      const averagePct = available > 0 ? Math.round((awarded / available) * 10000) / 100 : 0;
      const recommendation = averagePct < 50
        ? 'Re-teach this question pattern with worked examples and scaffolded follow-up tasks.'
        : averagePct < 70
          ? 'Run focused correction drills and mixed retrieval practice for this question type.'
          : 'Performance is stable; keep this question in spaced revision sets.';

      return {
        ...column,
        averagePct,
        recommendation,
        learnerCount: marklistRows.length,
        commentSample: comments.slice(0, 3)
      };
    });
  }, [questionColumns, marklistRows]);

  const toggleLearnerRow = (jobId) => {
    setExpandedLearnerRows((prev) => ({ ...prev, [jobId]: !prev[jobId] }));
  };

  if (markingTasks.length === 0) return null;

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-4">
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-sm font-semibold text-gray-900 inline-flex items-center">
          <BarChart3 className="w-4 h-4 mr-1.5 text-sigma-600" />
          Task Analysis
        </h3>
        <select
          value={selectedTaskId}
          onChange={(e) => setSelectedTaskId(e.target.value)}
          className="w-full max-w-xs border border-gray-300 rounded-md px-3 py-2 text-sm bg-white"
        >
          <option value="">Select task...</option>
          {markingTasks.map((task) => (
            <option key={task.id} value={task.id}>
              {task.taskName} ({task.subject || '-'} | Grade {task.grade || '-'})
            </option>
          ))}
        </select>
      </div>

      {selectedTaskId && marklistRows.length === 0 && (
        <div className="text-sm text-gray-500 border border-gray-200 rounded-md p-3">
          No completed script-marking results found for this task yet.
        </div>
      )}

      {selectedTaskId && marklistRows.length > 0 && (
        <>
          <div className="overflow-x-auto border border-gray-200 rounded-lg">
            <table className="w-full min-w-[980px] text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 uppercase">Learner</th>
                  {questionColumns.map((column) => (
                    <th key={column.label} className="px-3 py-2 text-left text-xs font-semibold text-gray-700 uppercase">
                      {column.display}
                    </th>
                  ))}
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 uppercase">Total</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 uppercase">%</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 uppercase">Level</th>
                </tr>
              </thead>
              <tbody>
                {marklistRows.map((row) => (
                  <React.Fragment key={row.jobId}>
                    <tr className="border-t border-gray-200">
                      <td className="px-3 py-2">
                        <button
                          type="button"
                          onClick={() => toggleLearnerRow(row.jobId)}
                          className="inline-flex items-center text-left text-sm font-medium text-gray-900 hover:text-sigma-700"
                        >
                          {expandedLearnerRows[row.jobId]
                            ? <ChevronDown className="w-4 h-4 mr-1" />
                            : <ChevronRight className="w-4 h-4 mr-1" />}
                          {row.learnerName}
                          {row.learnerId ? <span className="ml-1 text-xs text-gray-500">({row.learnerId})</span> : null}
                        </button>
                      </td>
                      {questionColumns.map((column) => {
                        const cell = row.byQuestion[column.label] || { awarded: 0, available: 0 };
                        return (
                          <td key={`${row.jobId}-${column.label}`} className="px-3 py-2 text-gray-700">
                            {cell.awarded}/{cell.available}
                          </td>
                        );
                      })}
                      <td className="px-3 py-2 font-semibold text-gray-900">{row.totalScore}/{row.maxScore}</td>
                      <td className={`px-3 py-2 font-semibold ${scoreColor(row.percentage)}`}>{row.percentage}%</td>
                      <td className="px-3 py-2 font-semibold text-gray-900">{row.level}</td>
                    </tr>

                    {expandedLearnerRows[row.jobId] && (
                      <tr className="bg-gray-50 border-t border-gray-100">
                        <td colSpan={questionColumns.length + 4} className="px-3 py-3">
                          <p className="text-xs font-semibold text-gray-700 mb-2">Response analysis</p>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                            {questionColumns.map((column) => {
                              const cell = row.byQuestion[column.label] || { awarded: 0, available: 0, comments: [] };
                              return (
                                <div key={`${row.jobId}-analysis-${column.label}`} className="border border-gray-200 rounded-md bg-white p-2">
                                  <div className="text-xs font-semibold text-gray-800 mb-1">Q{column.label}: {cell.awarded}/{cell.available}</div>
                                  <div className="text-xs text-gray-600">
                                    {cell.comments.length > 0 ? cell.comments.join(' | ') : 'No response comments captured.'}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            <div className="border border-gray-200 rounded-lg p-3 space-y-2">
              <h4 className="text-sm font-semibold text-gray-900">Learner Reports</h4>
              {learnerReports.map((report) => (
                <div key={`learner-${report.jobId}`} className="border border-gray-200 rounded-md p-2">
                  <p className="text-sm font-semibold text-gray-900">
                    {report.learnerName} - {report.percentage}% (Level {report.level})
                  </p>
                  <p className="text-xs text-gray-600 mt-1">
                    Strongest: {report.strongest.map((item) => `Q${item.label}`).join(', ') || 'N/A'}
                  </p>
                  <p className="text-xs text-gray-600">
                    Weakest: {report.weakest.map((item) => `Q${item.label}`).join(', ') || 'N/A'}
                  </p>
                  <p className="text-xs text-sigma-800 mt-1">Recommendation: {report.recommendation}</p>
                </div>
              ))}
            </div>

            <div className="border border-gray-200 rounded-lg p-3 space-y-2">
              <h4 className="text-sm font-semibold text-gray-900">Question Reports</h4>
              {questionReports.map((report) => (
                <div key={`question-${report.label}`} className="border border-gray-200 rounded-md p-2">
                  <p className="text-sm font-semibold text-gray-900">
                    Q{report.label} - Average {report.averagePct}%
                  </p>
                  <p className="text-xs text-gray-600 mt-1">Learners: {report.learnerCount}</p>
                  <p className="text-xs text-sigma-800 mt-1">Recommendation: {report.recommendation}</p>
                  {report.commentSample.length > 0 && (
                    <p className="text-xs text-gray-500 mt-1">Sample feedback: {report.commentSample.join(' | ')}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default TaskMarklistAnalysis;
